/**
 * Copyright (C) 2021 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */
import React, { useEffect, useState } from 'react';
import { Box } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import appTheme from '../../common/theme/appTheme.json';
import MissionDetailsForm from './MissionDetailsForm';
import ErrorMessage from '../../common/layout/ErrorMessage';
import {
  selectRequestAntennasError,
  selectRequestTerminalsError,
  selectLoadingTerminals,
  selectLoadingAntennas,
  selectLoadingMissionPlanById,
  selectMissionPlanByIdError,
  selectMissionDetailsLoading,
  selectDeleteConfirmation,
  selectSelectedMissionPlanId,
  selectUserId,
  selectSavingMissionPlanError,
  selectMissionPlanSavedOrUpdated,
  selectDeletingPlanError,
  selectDeletingPlanSuccess,
} from '../../../store';
import { useSelector, shallowEqual, useDispatch } from 'react-redux';
import {
  clearSavedMissionPlanById,
  clearSelectedAntenna,
  clearSelectedTerminal,
  setOverrideRecommendedAntenna,
  setMissionDetailsLoading,
  setMissionPlanSavedOrUpdated,
  deleteMissionConfirmation,
  clearDeletedMissionSuccess,
  clearDeletedMissionError,
  deleteMissionPlan,
} from '../../../store/missionPlans/actions';
import ModalLoadingSpinner from '../../common/layout/ModalLoadingSpinner';
import WarningIcon from '@mui/icons-material/Warning';
import { useHistory } from 'react-router-dom';
import MenuItems from '../../common/layout/menu/MenuItems';
import ModalDialog from '../../common/dialogs/ModalDialog';
export interface MissionDetailsProps {}

const useStyles = makeStyles({
  title: {
    color: appTheme.primaryTextColor,
    fontSize: '20px',
    fontWeight: 700,
    lineHeight: '28px',
    fontFamily: appTheme.viasatFontFamilyRegular,
    letterSpacing: '0.2px',
    marginTop: '52px',
  },
  disclaimer: {
    color: appTheme.primaryTextColor,
    fontFamily: appTheme.appFontFamily,
    marginTop: '5px',
    marginBottom: '10px',
  },
});

// Clear relevant redux fields onComponentUnmount
export function useClearMissionDetailReduxFields() {
  const dispatch = useDispatch();

  useEffect(() => {
    return () => {
      dispatch(clearSavedMissionPlanById());
      dispatch(clearSelectedAntenna());
      dispatch(clearSelectedTerminal());
      dispatch(setOverrideRecommendedAntenna(true));
    };
  }, [dispatch]);
}

// Set redux Mission Details loading field
export function useSetMissionDetailsLoading() {
  const loadingAntennas = useSelector(selectLoadingAntennas, shallowEqual);
  const loadingTerminals = useSelector(selectLoadingTerminals, shallowEqual);
  const loadingMissionPlanById = useSelector(selectLoadingMissionPlanById, shallowEqual);
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(setMissionDetailsLoading(loadingAntennas || loadingTerminals || loadingMissionPlanById));
  }, [loadingTerminals, loadingAntennas, loadingMissionPlanById, dispatch]);
}

const MissionDetails = (props: MissionDetailsProps) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();
  const antennasError = useSelector(selectRequestAntennasError, shallowEqual);
  const terminalsError = useSelector(selectRequestTerminalsError, shallowEqual);
  const missionPlansByIdError = useSelector(selectMissionPlanByIdError, shallowEqual);
  const loadingMissionDetails = useSelector(selectMissionDetailsLoading, shallowEqual);
  const savingMissionPlanError = useSelector(selectSavingMissionPlanError, shallowEqual);
  const missionPlanSavedOrUpdated = useSelector(selectMissionPlanSavedOrUpdated, shallowEqual);
  const deleteModal = useSelector(selectDeleteConfirmation, shallowEqual);
  const missionPlanId = useSelector(selectSelectedMissionPlanId, shallowEqual);
  const userId = useSelector(selectUserId, shallowEqual);
  const deletingError = useSelector(selectDeletingPlanError, shallowEqual);
  const deletingSuccess = useSelector(selectDeletingPlanSuccess, shallowEqual);
  const [deleteModalHeader, setDeleteModalHeader] = useState<string>('Confirm Mission Plan Deletion');
  const [deleteModalBody, setDeleteModalBody] = useState<JSX.Element>(
    <>
      Mission Plan will be deleted and all Mission Details and Flight Leg Data will be lost.
      <br />
      <br />
      This action cannot be undone.
    </>
  );
  const [hideRightBtn, setHideRightBtn] = useState<boolean>(false);
  const [deleteClick, setDeleteClick] = useState<boolean>(false);

  useEffect(() => {
    if (missionPlanSavedOrUpdated) {
      history.push(`/${MenuItems.plans}/flight-information`);
    }

    return () => {
      dispatch(setMissionPlanSavedOrUpdated(false));
    };
  }, [missionPlanSavedOrUpdated, history, dispatch]);

  // Clear relevant redux fields on unmount
  useClearMissionDetailReduxFields();

  // Set redux Mission Details loading field
  useSetMissionDetailsLoading();

  // On successful deletion
  useEffect(() => {
    if (deletingSuccess) {
      dispatch(clearSavedMissionPlanById());
      dispatch(deleteMissionConfirmation(false));
      dispatch(clearDeletedMissionSuccess());
      history.push(`/${MenuItems.plans}/`);
    }
  }, [deletingSuccess, dispatch, history, userId]);

  // On unsuccessful deletion
  useEffect(() => {
    if (deletingError) {
      setHideRightBtn(true);
      dispatch(clearDeletedMissionError());
      setDeleteModalHeader('Something went wrong');
      setDeleteModalBody(<>Unable to delete mission plan. Please try again.</>);
    }
  }, [deletingError, dispatch]);

  // Changes Delete text when the delete button is clicked
  useEffect(() => {
    setDeleteModalBody(
      <>
        Mission Plan will be deleted and all Mission Details and Flight Leg Data will be lost.
        <br />
        <br />
        This action cannot be undone.
      </>
    );
  }, [deleteClick]);

  return (
    <React.Fragment>
      {savingMissionPlanError ? <ErrorMessage>An error occurred while saving mission details.</ErrorMessage> : null}
      {terminalsError && !antennasError ? (
        <ErrorMessage>Can't fetch terminals. Please reload page.</ErrorMessage>
      ) : null}
      {antennasError && !terminalsError ? <ErrorMessage>Can't fetch antennas. Please reload page.</ErrorMessage> : null}
      {antennasError && terminalsError ? (
        <ErrorMessage>Can't fetch terminals and antennas. Please reload page.</ErrorMessage>
      ) : null}

      {missionPlansByIdError ? (
        <ErrorMessage>Unable to retrieve mission plan fields. Please reload page.</ErrorMessage>
      ) : null}
      <Box className={classes.title}>Input Mission Details</Box>
      <Box className={classes.disclaimer}>
        * Are <strong>required</strong> fields. Note: Missions are <strong>deleted 30 days after</strong> creation
      </Box>
      <Box className={classes.disclaimer}>
        <b>Please note:</b> An HNA request is <b>not</b> required to operate on the HCX network on the ground in
        Afghanistan, Azerbaijan, Canada, Djibouti, Ireland, Japan, Jordan, The Philippines, The United Kingdom, or
        Ukraine. An HNA request <b>is</b> required to operate on VI on the ground in Guatemala.
      </Box>
      <MissionDetailsForm />
      {loadingMissionDetails ? <ModalLoadingSpinner /> : null}
      <ModalDialog
        open={deleteModal}
        leftBtnText='Cancel'
        rightBtnText='Delete'
        hideRightBtn={hideRightBtn}
        // cancel
        onLeftBtnClicked={() => {
          dispatch(deleteMissionConfirmation(false));
          setDeleteClick(!deleteClick);
          setHideRightBtn(false);
        }}
        // confirm delete
        onRightBtnClicked={() => {
          if (missionPlanId && userId) {
            dispatch(deleteMissionPlan(missionPlanId, userId));
          }
        }}
        title={
          <Box display={'flex'} gap={'8px'} alignItems={'center'}>
            <WarningIcon htmlColor={'#FFB029'} /> {deleteModalHeader}
          </Box>
        }
        message={deleteModalBody}
      />
    </React.Fragment>
  );
};

export default MissionDetails;
